/* The landing page's small product examples, drawn with HTML and CSS (no images, nothing fetched). They show what the
   app looks like with an example student, carry an "Example" tag, and are hidden from screen readers: the text next to
   each one says the same thing in words. University names stay in English in every language, as in the app. */
import type { ReactNode } from 'react';
import type { Lang } from '@pathabroad/core/browser';
import { useT } from '../../app/i18n';
import { Icon } from '../../app/icons';
import { countryName } from '../../lib/format';
import { translate, type MessageKey } from '../../lib/i18n';
import globeUrl from './globe.svg';
import { GLOBE_SIZE, ROUTES } from './globe';

/* the planner's three chance bands, with its own names for them */
type Band = 'likely' | 'fit' | 'ambitious';
const BAND: Record<Band, { key: MessageKey; chip: string }> = {
  likely: { key: 'land.demo.likely', chip: 'chip--green' },
  fit: { key: 'land.demo.fit', chip: 'chip--blue' },
  ambitious: { key: 'land.demo.ambitious', chip: 'chip--orange' },
};

type School = { name: string; short: string; cc: string; band: Band };
export const SCHOOLS: School[] = [
  { name: 'University of Toronto', short: 'UT', cc: 'CA', band: 'fit' },
  { name: 'The University of Manchester', short: 'UM', cc: 'GB', band: 'likely' },
  { name: 'Technical University of Munich', short: 'TU', cc: 'DE', band: 'fit' },
  { name: 'The University of Melbourne', short: 'UM', cc: 'AU', band: 'ambitious' },
];

/* a university's name in English, kept left-to-right inside Arabic text */
export const Uni = ({ children }: { children: string }) => <bdi lang="en" className="mock__uni">{children}</bdi>;

export function Tag() {
  const { t } = useT();
  return <span className="mock__tag">{t('land.demo.example')}</span>;
}

function Bar({ title, icon, end }: { title: string; icon?: ReactNode; end?: ReactNode }) {
  return (
    <div className="mock__bar">
      {icon && <span className="mock__icon">{icon}</span>}
      <span className="mock__title">{title}</span>
      {end ?? <Tag />}
    </div>
  );
}

export function SchoolRow({ s, meter }: { s: School; meter?: boolean }) {
  const { t, lang } = useT();
  const b = BAND[s.band];
  return (
    <li className="mock-school">
      <span className={`mock-school__badge mock-school__badge--${s.cc.toLowerCase()}`} dir="ltr">{s.short}</span>
      <span className="mock-school__main">
        <Uni>{s.name}</Uni>
        <span className="mock-school__sub">{countryName(s.cc, lang)}</span>
        {meter && <span className="mock-meter"><span className={`mock-meter__fill mock-meter__fill--${s.band}`} /></span>}
      </span>
      <span className={`chip ${b.chip}`}>{t(b.key)}</span>
    </li>
  );
}

/* The hero: a globe at night, land drawn as dots (globe.svg), with the routes students take from home to a university.
   The example student's is lit: Amina's, from Lagos (LOS) to Toronto (YYZ), from the beacon's blue to the destination's
   magenta. Around it, three pieces of the app: the admissions email with her offer, a WhatsApp reminder and her matches.
   Geography doesn't mirror, so the globe is the same in Arabic. */
export function HeroStage() {
  const { t, rich } = useT();
  const [trip, ...others] = ROUTES;
  const cities = [...new Map(others.flatMap(r => [r.from, r.to]).map(p => [p.join(), p])).values()]
    .filter(p => p.join() !== trip.from.join() && p.join() !== trip.to.join());
  return (
    <div className="stage night" aria-hidden="true">
      <div className="stage__sphere">
        <img className="stage__globe" src={globeUrl} alt="" width={GLOBE_SIZE} height={GLOBE_SIZE} decoding="async" />
        <svg className="stage__routes" viewBox={`0 0 ${GLOBE_SIZE} ${GLOBE_SIZE}`} focusable="false">
          <defs>
            <linearGradient id="pa-trip" gradientUnits="userSpaceOnUse" x1={trip.from[0]} y1={trip.from[1]} x2={trip.to[0]} y2={trip.to[1]}>
              <stop offset="0" stopColor="#57C7FF" />
              <stop offset="1" stopColor="#EF5DE0" />
            </linearGradient>
            <filter id="pa-glow" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {others.map(r => <path key={r.id} className="stage__route" d={r.d} pathLength={1} />)}
          {cities.map(([x, y]) => <circle key={`${x},${y}`} className="stage__city" cx={x} cy={y} r={2.6} />)}
          <path className="stage__trip" d={trip.d} pathLength={1} />
          <circle className="stage__home" cx={trip.from[0]} cy={trip.from[1]} r={6} />
          <circle className="stage__ping" cx={trip.to[0]} cy={trip.to[1]} r={9} />
          <circle className="stage__away" cx={trip.to[0]} cy={trip.to[1]} r={9} />
          <Code x={trip.from[0] + 34} y={trip.from[1] + 26} text="LOS" />
          <Code x={trip.to[0] - 4} y={trip.to[1] + 34} text="YYZ" />
        </svg>
      </div>
      <div className="stage__panels">
        <div className="mock mock--mail">
          <Bar title={t('land.demo.inbox')} icon={<Icon.inbox />} end={<span className="mock__time">09:41</span>} />
          <div className="mock-mail">
            <span className="chip chip--green">{t('land.demo.decision')}</span>
            <Uni>University of Toronto</Uni>
            <span className="mock-mail__text">{t('land.demo.offer')}</span>
            <span className="mock-mail__next"><Icon.ok /> {t('land.demo.nextReply')}</span>
          </div>
        </div>
        <div className="mock mock--wa">
          <div className="mock-wa__head"><span className="mock-wa__dot" />{t('land.demo.whatsapp')}<span className="mock__time">09:00</span></div>
          <p className="mock-wa__bubble">{rich('land.demo.reminder', { school: <Uni>University of Toronto</Uni> })}</p>
        </div>
        <div className="mock mock--matches">
          <Bar title={t('land.demo.matches')} icon={<Icon.map />} />
          <ul className="mock-list">
            {SCHOOLS.slice(0, 3).map(s => <SchoolRow key={s.name} s={s} />)}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* an airport code on the globe, in a small tag */
function Code({ x, y, text }: { x: number; y: number; text: string }) {
  return (
    <g className="stage__code" transform={`translate(${x} ${y})`}>
      <rect x={-23} y={-12} width={46} height={24} rx={7} />
      <text textAnchor="middle" dominantBaseline="central" y={1}>{text}</text>
    </g>
  );
}

/* finding your fit: the list with chance bands and a meter, and the honest footnote */
export function DemoMatches() {
  const { t } = useT();
  return (
    <div className="mock" aria-hidden="true">
      <Bar title={t('land.demo.matches')} icon={<Icon.map />} />
      <div className="mock-filters">
        <span className="mock-filter mock-filter--on">{t('land.demo.all')}</span>
        <span className="mock-filter">{t('land.demo.likely')}</span>
        <span className="mock-filter">{t('land.demo.fit')}</span>
        <span className="mock-filter">{t('land.demo.ambitious')}</span>
      </div>
      <ul className="mock-list">
        {SCHOOLS.slice(0, 3).map(s => <SchoolRow key={s.name} s={s} meter />)}
      </ul>
      <p className="mock__foot">{t('land.demo.estimate')}</p>
    </div>
  );
}

/* the Chrome extension filling a portal form: saved answers in, the essay and the submit button left to the student */
export function DemoApply() {
  const { t, lang } = useT();
  const field = (label: string, value: string | null, note?: string) => (
    <div className={value ? 'mock-field mock-field--filled' : 'mock-field mock-field--yours'}>
      <span className="mock-field__label">{label}</span>
      <span className="mock-field__value">
        {value ? <><bdi>{value}</bdi><Icon.check /></> : <span className="mock-field__empty">{note}</span>}
      </span>
    </div>
  );
  return (
    <div className="browser" aria-hidden="true">
      <div className="browser__bar" dir="ltr">
        <span className="browser__dots"><i /><i /><i /></span>
        <span className="browser__url">apply.commonapp.org</span>
      </div>
      <div className="browser__body">
        <div className="mock-form">
          {field(t('land.demo.firstName'), 'Amina')}
          {field(t('land.demo.lastName'), 'Haddad')}
          {field(t('land.demo.citizenship'), countryName('JO', lang))}
          {field(t('land.demo.essay'), null, t('land.demo.youWrite'))}
          <div className="mock-form__foot">
            <span className="mock-submit">{t('land.demo.submit')}</span>
            <span className="mock-form__hint">{t('land.demo.youSubmit')}</span>
          </div>
        </div>
        <div className="mock-ext">
          <div className="mock-ext__head"><MiniMark /><bdi lang="en">PathsAbroad</bdi></div>
          <span className="mock-ext__btn">{t('land.demo.fill')}</span>
          <span className="mock-ext__ok"><Icon.ok /> {t('land.demo.filled')}</span>
        </div>
      </div>
    </div>
  );
}

/* the application inbox: three kinds of email, sorted, and the next step added to the to-do list */
export function DemoInbox() {
  const { t } = useT();
  const rows: Array<[string, MessageKey, string, MessageKey]> = [
    ['chip--green', 'land.demo.decision', 'University of Toronto', 'land.demo.offer'],
    ['chip--blue', 'land.demo.interview', 'The University of Manchester', 'land.demo.interviewText'],
    ['chip--orange', 'land.demo.missing', 'Technical University of Munich', 'land.demo.missingText'],
  ];
  return (
    <div className="mock" aria-hidden="true">
      <Bar title={t('land.demo.inbox')} icon={<Icon.inbox />} />
      <p className="mock-addr" dir="ltr">amina.7k2@apply.pathsabroad.com</p>
      <ul className="mock-mails">
        {rows.map(([chip, cat, uni, text]) => (
          <li key={uni} className="mock-mails__row">
            <span className={`chip ${chip}`}>{t(cat)}</span>
            <Uni>{uni}</Uni>
            <span className="mock-mails__text">{t(text)}</span>
          </li>
        ))}
      </ul>
      <div className="mock-todo"><Icon.ok /><span><strong>{t('land.demo.todo')}</strong> {t('land.demo.missingText')}</span></div>
    </div>
  );
}

/* the AI guide: a question, and an answer with its AI label */
export function DemoChat() {
  const { t } = useT();
  return (
    <div className="mock-chat" aria-hidden="true">
      <p className="mock-chat__me">{t('land.demo.aiQ')}</p>
      <p className="mock-chat__ai"><span className="chip chip--ai">{t('ai.tag')}</span> {t('land.demo.aiA')}</p>
    </div>
  );
}

/* WhatsApp reminders before a deadline, and STOP to end them */
export function DemoReminder() {
  const { t, rich } = useT();
  return (
    <div className="mock-wa mock-wa--inline" aria-hidden="true">
      <p className="mock-wa__bubble">{rich('land.demo.reminder', { school: <Uni>University of Toronto</Uni> })}</p>
      <p className="mock-wa__bubble mock-wa__bubble--me" dir="ltr">STOP</p>
    </div>
  );
}

/* the visa checklist, part done */
export function DemoChecklist() {
  const { t } = useT();
  const items: Array<[MessageKey, boolean]> = [['land.demo.visa1', true], ['land.demo.visa2', true], ['land.demo.visa3', false], ['land.demo.visa4', false]];
  return (
    <ul className="mock-check" aria-hidden="true">
      {items.map(([k, done]) => (
        <li key={k} className={done ? 'is-done' : undefined}><span className="mock-check__box">{done && <Icon.check />}</span>{t(k)}</li>
      ))}
    </ul>
  );
}

/* the parent's read-only page: where each application stands */
export function DemoParent() {
  const { t } = useT();
  const rows: Array<[string, string, MessageKey]> = [
    ['University of Toronto', 'chip--green', 'app.decision_admitted'],
    ['The University of Manchester', 'chip--blue', 'app.submitted'],
    ['Technical University of Munich', 'chip--orange', 'app.in_progress'],
  ];
  return (
    <div className="mock-parent" aria-hidden="true">
      <p className="mock-parent__head"><Icon.people /> {t('land.demo.parentView')}</p>
      <ul>
        {rows.map(([uni, chip, k]) => <li key={uni}><Uni>{uni}</Uni><span className={`chip ${chip}`}>{t(k)}</span></li>)}
      </ul>
    </div>
  );
}

/* the same line in every language, each in its own direction */
export function DemoLanguages() {
  const langs: Lang[] = ['en', 'ar', 'hi', 'id'];
  return (
    <ul className="mock-langs" aria-hidden="true">
      {langs.map(l => <li key={l} lang={l} dir={l === 'ar' ? 'rtl' : 'ltr'}>{translate(l, 'home.title')}</li>)}
    </ul>
  );
}

/* field encryption: what the student sees, and the token the database stores (v1:key:iv:tag:ciphertext) */
export function DemoEncryption() {
  const { t } = useT();
  return (
    <div className="mock-enc" aria-hidden="true">
      <div className="mock-enc__row"><span className="mock-enc__k">{t('land.demo.youSee')}</span><code dir="ltr">P1234567</code></div>
      <div className="mock-enc__arrow" dir="ltr"><span>AES-256-GCM</span></div>
      <div className="mock-enc__row"><span className="mock-enc__k">{t('land.demo.stored')}</span><code dir="ltr">v1:k1:9fQxW2…:Tz8wq…:Lr4mD…</code></div>
    </div>
  );
}

/* the mark at a small size (no gradient: ids must stay unique on the page) */
export function MiniMark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" className="mini-mark">
      <rect width="32" height="32" rx="9" fill="#0D1322" />
      <rect x=".5" y=".5" width="31" height="31" rx="8.5" fill="none" stroke="#9DB2F0" strokeOpacity=".35" />
      <path d="M9.5 22.5c.5-5 3.6-6.4 6.5-6.9s6-1.8 6.6-6.6" fill="none" stroke="#9DB2F0" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="0.2 4.2" />
      <circle cx="9.5" cy="22.5" r="2.7" fill="#57C7FF" />
      <circle cx="22.6" cy="9" r="3" fill="none" stroke="#EF5DE0" strokeWidth="2.2" />
    </svg>
  );
}
